import React, { useState, useEffect, useCallback } from 'react';
import { commercialDashboardService } from '../services/api';
import { toast } from 'sonner';
import { FileText, CheckCircle2, XCircle, Clock, TrendingUp, BarChart3 } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

const STATUS_COLORS = {
  accettata: '#10b981',
  rifiutata: '#ef4444',
  inviata: '#3b82f6',
  in_preparazione: '#f59e0b',
  da_valutare: '#94a3b8',
};

const STATUS_LABELS = {
  accettata: 'Accettata',
  rifiutata: 'Rifiutata',
  inviata: 'Inviata',
  in_preparazione: 'In preparazione',
  da_valutare: 'Da valutare',
};

export default function CommercialDashboardPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const years = [];
  for (let y = new Date().getFullYear(); y >= 2020; y--) years.push(y);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await commercialDashboardService.getData(year);
      setData(res.data);
    } catch {
      toast.error('Errore caricamento dashboard');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-8 text-center text-slate-400">Caricamento...</div>;
  if (!data) return <div className="p-8 text-center text-slate-400">Nessun dato disponibile</div>;

  const monthBarData = data.offers_per_month.map((v, i) => ({ name: MONTHS[i], offerte: v }));
  const leadTimeLineData = data.lead_time_per_month.map((v, i) => ({ name: MONTHS[i], giorni: v }));

  const pieData = Object.entries(data.status_counts || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: STATUS_LABELS[k] || k, value: v, color: STATUS_COLORS[k] || '#94a3b8' }));

  const KpiCard = ({ icon: Icon, label, value, sub, color }) => (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-3" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">{label}</p>
        <p className="text-xl font-bold text-slate-900 mt-0.5">{value}</p>
        {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="commercial-dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="dashboard-title">Cruscotto Commerciale</h1>
          <p className="text-sm text-slate-500 mt-0.5">Panoramica performance offerte {year}</p>
        </div>
        <select value={year} onChange={e => setYear(parseInt(e.target.value))}
          className="text-sm border border-slate-200 rounded-md px-3 py-2 bg-white font-medium" data-testid="year-select">
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="kpi-cards">
        <KpiCard icon={FileText} label="Totale Offerte" value={data.total_offers} color="bg-slate-100 text-slate-600" />
        <KpiCard icon={CheckCircle2} label="Accettate" value={data.accepted} sub={data.acceptance_rate !== null ? `${data.acceptance_rate}% tasso accettazione` : undefined} color="bg-emerald-100 text-emerald-600" />
        <KpiCard icon={XCircle} label="Rifiutate" value={data.rejected} color="bg-red-100 text-red-600" />
        <KpiCard icon={TrendingUp} label="Aperte" value={data.open} color="bg-blue-100 text-blue-600" />
        <KpiCard icon={Clock} label="Lead Time Medio" value={data.avg_lead_time_days !== null ? `${data.avg_lead_time_days}g` : '-'} color="bg-amber-100 text-amber-600" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Offers per month - Bar Chart */}
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="chart-offers-month">
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-slate-400" /> Offerte per Mese
          </h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthBarData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }} />
              <Bar dataKey="offerte" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Offerte" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Lead Time per month - Line Chart */}
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="chart-leadtime-month">
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-400" /> Lead Time Medio per Mese (giorni)
          </h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={leadTimeLineData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }} />
              <Line type="monotone" dataKey="giorni" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} name="Giorni" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Status Pie Chart */}
      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="chart-status-breakdown">
        <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-4">Ripartizione per Stato</h3>
        {pieData.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">Nessuna offerta per l'anno selezionato</p>
        ) : (
          <div className="flex flex-col md:flex-row items-center gap-6">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={55} paddingAngle={2} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={{ stroke: '#94a3b8' }}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
